import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useParserConfig } from '@/hooks/useParserConfig';
import { Loader2, Check, X, Info } from 'lucide-react';

const PARSER_OPTIONS = [
  { value: 'openai', label: 'OpenAI GPT-4o-mini' },
  { value: 'mistral', label: 'Mistral Large' },
  { value: 'openrouter', label: 'OpenRouter (Claude)' },
  { value: 'deepseek', label: 'DeepSeek' },
];

export function ParserConfigPanel() {
  const { config, isLoading, updateConfig, isUpdating } = useParserConfig();
  
  const [primaryParser, setPrimaryParser] = useState<string>(config?.primary_parser || 'openai');
  const [temperature, setTemperature] = useState(config?.temperature || 0.1);
  const [maxTokens, setMaxTokens] = useState(config?.max_tokens || 4000);
  const [timeout, setTimeout] = useState(config?.timeout_seconds || 120);

  const handleSave = async () => {
    await updateConfig({
      primary_parser: primaryParser as any,
      temperature,
      max_tokens: maxTokens,
      timeout_seconds: timeout,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Manual Parsing Trigger</AlertTitle>
        <AlertDescription>
          AI parsing is no longer automatic. Bills will wait in the queue for manual trigger or will use Vendor API if configured.
          Configure Vendor APIs in the Sources tab for automatic parsing.
        </AlertDescription>
      </Alert>
      
      <Card>
        <CardHeader>
          <CardTitle>AI Parser Configuration</CardTitle>
          <CardDescription>
            Configure which AI models to use for bill parsing and their parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="primary-parser">Primary Parser</Label>
              <Select value={primaryParser} onValueChange={setPrimaryParser}>
                <SelectTrigger id="primary-parser">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARSER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                First model to attempt bill parsing
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeout">Timeout (seconds)</Label>
              <Input
                id="timeout"
                type="number"
                value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value) || 120)}
                min={30}
                max={300}
              />
              <p className="text-xs text-muted-foreground">
                Maximum time to wait for parsing response
              </p>
            </div>

            <div className="space-y-2">
              <Label>Temperature: {temperature.toFixed(2)}</Label>
              <Slider
                value={[temperature]}
                onValueChange={(v) => setTemperature(v[0])}
                min={0}
                max={1}
                step={0.1}
              />
              <p className="text-xs text-muted-foreground">
                Lower = more deterministic, Higher = more creative
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max-tokens">Max Tokens</Label>
              <Input
                id="max-tokens"
                type="number"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value) || 4000)}
                min={500}
                max={8000}
              />
              <p className="text-xs text-muted-foreground">
                Maximum response length from AI model
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API Key Status</CardTitle>
          <CardDescription>Current status of configured AI provider API keys</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {PARSER_OPTIONS.map((option) => (
              <div
                key={option.value}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="font-medium">{option.label}</div>
                  <Badge variant="default" className="gap-1">
                    <Check className="h-3 w-3" />
                    Configured
                  </Badge>
                </div>
                <Button variant="outline" size="sm">
                  Test Connection
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Parser</CardTitle>
          <CardDescription>
            Upload a sample bill to test extraction with current settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1">
                Upload Sample Bill
              </Button>
              <Button variant="outline" className="flex-1">
                Select from Recent Bills
              </Button>
            </div>
            <Button className="w-full">Run Test Extraction</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
